import { InstrumentApp } from "./apps/InstrumenApp.js";
import { IntroductionApp } from "./apps/IntroductionApp.js";
import { LayoutApp } from "./apps/LayoutApp.js";
import { MelodyApp } from "./apps/MelodyApp.js";
import { MidiApp } from "./apps/MidiApp.js";
import { OrchestraApp } from "./apps/OrchestraApp.js";
import { SelectorApp } from "./apps/SelectorApp.js";
import { ShapeApp } from "./apps/ShapeApp.js";
import { TestApp } from "./apps/TestApp.js";
import { Login, LoginRole } from "./display/Login.js";

export interface AppInfo {
    name: string;
    icon?: string;
    key: string;
    roles: LoginRole[] | "*";
    showInSelector?: boolean;
    factory: () => void;
}

export class Application {

    constructor(private _element: HTMLDivElement) {
        e5.console.log("[eventfive.aheads.phil] [Application]");

        this.init();
    }

    private async init() {

        this.resize();

        const isRuntime = Boolean(window.api);
        const debug = e5.console.enabled || e5.Url.params.has("debug");
        e5.console.enabled = debug;

        window.addEventListener("resize", () => this.resize());

        // const isWpAdmin = this._element.getAttribute("data-isloggedin") === 'on';

        let role: LoginRole = null;
        if (isRuntime) {
            role = "runtime";
        } else if (location.hostname === "127.0.0.1") {
            role = "joeran.benker";
        } else {
            const login = new Login({
                container: document.body
            });
            role = await login.wait();
            login.dispose();
        }

        const appInfos: AppInfo[] = [
            {
                name: "App Selector",
                key: "index",
                roles: "*",
                showInSelector: false,
                factory: () => {
                    new SelectorApp({
                        element: this._element,
                        appInfos: appInfos,
                        role: role
                    });
                }
            },
            {
                name: "Orchestra",
                key: "orchestra",
                roles: [
                    "runtime",
                    "annika.meyer",
                    "joeran.benker",
                    "lars.grochler",
                    "public"
                ],
                factory: () => {
                    new OrchestraApp({
                        element: this._element
                    })
                }
            },
            {
                name: "Melody",
                key: "melody",
                roles: [
                    "runtime",
                    "joeran.benker",
                    "annika.meyer",
                    "lars.grochler",
                    "public"
                ],
                factory: () => {
                    new MelodyApp({
                        element: this._element,
                        debug: debug
                    })
                }
            },
            {
                name: "Introduction",
                key: "introduction",
                roles: [
                    "runtime",
                    "joeran.benker",
                    "annika.meyer",
                    "lars.grochler",
                    "public"
                ],
                factory: () => {
                    new IntroductionApp({
                        element: this._element
                    })
                }
            },
            {
                name: "Shape",
                key: "shape",
                roles: [
                    "joeran.benker",
                    "annika.meyer",
                    "lars.grochler"
                ],
                factory: () => {
                    new ShapeApp({
                        element: this._element
                    })
                }
            },
            {
                name: "Layout",
                key: "layout",
                roles: [
                    "joeran.benker",
                    "annika.meyer",
                    "lars.grochler"
                ],
                factory: () => {
                    new LayoutApp({
                        element: this._element,
                        editable: this._element.getAttribute("data-isloggedin") === 'on',
                        restUrl: this._element.getAttribute("data-layout")
                    })
                }
            },
            {
                name: "Instrument",
                key: "instrument",
                roles: [
                    "joeran.benker",
                    "annika.meyer"
                ],
                factory: () => {
                    new InstrumentApp({
                        element: this._element
                    })
                }
            },
            {
                name: "Test",
                key: "test",
                roles: [
                    "joeran.benker",
                    "annika.meyer"
                ],
                factory: () => {
                    new TestApp({
                        element: this._element
                    })
                }
            },
            {
                name: "Midi",
                key: "midi",
                roles: [
                    "joeran.benker",
                    "annika.meyer",
                    "lars.grochler"
                ],
                factory: () => {
                    new MidiApp({
                        element: this._element,
                        restUrl: this._element.getAttribute("data-melodynotes")
                    })
                }
            },
        ];

        const appKey = e5.Url.params.get("app", "index");
        const info = appInfos.find((v) => v.key === appKey);
        if (!info) {
            console.warn("Could not find app by key", appKey);
            e5.Toast.showText("Anwendung nicht gefunde.");
            return;
        }

        if (info.roles === "*"
            || info.roles.indexOf(role) !== -1) {
            info.factory();
        } else {
            console.warn("Access denied for", appKey);
            e5.Toast.showText("Zugriff verweigert.");
            return;
        }
    }

    private resize(): void {
        const w = window.innerWidth;
        const h = window.innerHeight;
        const elementWidth = 1920;
        const elementHeight = 1080;
        const scale = Math.max(Math.min(w / elementWidth, h / elementHeight), 0.05);

        if (scale === 1) {
            document.documentElement.setAttribute("data-scale", "fit");
        } else if (scale > 1) {
            document.documentElement.setAttribute("data-scale", "up");
        } else if (scale < 1) {
            document.documentElement.setAttribute("data-scale", "down");
        }

        document.documentElement.style.setProperty("--stage-scale", scale + "");
        // document.documentElement.style.setProperty("--stage-transform", (scale === 1 ? "none" : `scale(${scale})`));
        // document.documentElement.style.setProperty("--stage-scale-clamped", Math.min(scale, 1) + "");
        // document.documentElement.style.setProperty("--stage-scale-inverse", (1 / scale) + "");
    }

}

document.addEventListener("DOMContentLoaded", () => {
    var e = document.querySelector<HTMLDivElement>("#app");
    if (e) {
        new Application(e);
    }
});